import React, { useMemo, useState, useCallback } from "react";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { DataTable } from "../DataTable";
import { PageChanger } from "../PageChanger";
import { SearchBar } from "../SearchBar";
import {
  ActionToolConfig,
  ActionToolSettingsInput,
  InternalService,
  NodeDefinition,
  NodesService,
} from "@/generated";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeftIcon, TriangleAlert } from "lucide-react";
import { TruncatedTextWithTooltip } from "../TruncatedTextWithTooltip";
import { AnimatePresence, motion } from "framer-motion";
import { NoxusInput } from "../Form/InputField";
import BaseDialog from "../Modals/BaseDialog";
import { ActionConfigurationStep } from "./ActionConfigurationStep";
import { useSpotUser } from "@/utils/spotUser";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { NoxusTooltip } from "../UI/NoxusTooltip";
import { Separator } from "../shadcn/separator";
import { useEditorReadOnly } from "@/hooks/useEditorReadOnly";

const PAGE_SIZE = 8;

const columnHelper = createColumnHelper<NodeDefinition>();

interface ActionPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectAction: (props: {
    node_name: string;
    value: string;
    tool_config: ActionToolConfig | null;
  }) => void;
  openActions: {
    id: string;
    name: string;
  }[];
  editingTool?: ActionToolSettingsInput;
}

export const ActionPickerModal = ({
  isOpen,
  onClose,
  onSelectAction,
  openActions,
  editingTool,
}: ActionPickerProps) => {
  const { spotGroup } = useSpotUser();
  // Defensive: read-only viewers don't see the "add action" button so the
  // modal shouldn't be rendered, but if it is, skip integrations:edit-only
  // fetches that would 403.
  const isReadOnly = useEditorReadOnly();
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [toolConfig, setToolConfig] = useState<ActionToolConfig | null>(
    editingTool?.tool_config || null
  );

  const { data: integrationsData } = useRemoteData(
    DATA_KEYS.INTEGRATIONS,
    { path: { group_id: spotGroup.id } },
    { enabled: !isReadOnly }
  );

  const hasMissingIntegrations = useCallback(
    (action: NodeDefinition): boolean => {
      if (!integrationsData || !action.integrations) return false;
      const activeIntegrations = Object.keys(integrationsData).filter(
        (key) => integrationsData[key as keyof typeof integrationsData]
      );
      return (action.integrations as Array<string | string[]>).some(
        (requirement) => {
          if (Array.isArray(requirement)) {
            return requirement.every(
              (integration) => !activeIntegrations.includes(integration)
            );
          }
          return !activeIntegrations.includes(requirement);
        }
      );
    },
    [integrationsData]
  );

  const columns: ColumnDef<NodeDefinition, any>[] = useMemo(
    () => [
      columnHelper.accessor("title", {
        cell: (info) => {
          const image = info.row.original?.image;
          const needsIntegration = hasMissingIntegrations(info.row.original);
          return (
            <div className="flex gap-2 items-center">
              {image && (
                <img
                  src={image}
                  alt={info.getValue() || ""}
                  className="w-6 h-6 rounded"
                />
              )}
              <TruncatedTextWithTooltip>
                {info.getValue()}
              </TruncatedTextWithTooltip>
              {/* {needsIntegration && (
                <NoxusTooltip content="Missing required integration">
                  <TriangleAlert
                    size={16}
                    className="text-orange-500 flex-shrink-0"
                  />
                </NoxusTooltip>
              )} */}
            </div>
          );
        },
        header: "Name",
      }),
      columnHelper.accessor("description", {
        cell: (info) => (
          <p className="text-sm text-muted-foreground line-clamp-2 text-wrap">
            {info.getValue() || "-"}
          </p>
        ),
        header: "Description",
      }),
    ],
    [hasMissingIntegrations]
  );

  const { data: actionsData, isLoading: isLoadingActions } = useQuery({
    queryKey: ["nodes", "tools", search],
    queryFn: () => InternalService.getNodes({ query: { is_tool: true } }),
    staleTime: Infinity,
  });

  const filteredActionsData = useMemo(() => {
    if (!actionsData?.data) return [];

    let filtered = actionsData.data;

    filtered = filtered.filter(
      (action) => action.visible !== false && action.is_available !== false
    );

    if (editingTool) {
      filtered = filtered.filter(
        (action) => action.type === editingTool.node_name
      );
    }

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (action) =>
          action.title?.toLowerCase().includes(searchLower) ||
          action.description?.toLowerCase().includes(searchLower) ||
          action.category?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [actionsData, search, editingTool]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    return filteredActionsData.slice(startIndex, endIndex);
  }, [filteredActionsData, currentPage]);

  const totalPages = Math.ceil(filteredActionsData.length / PAGE_SIZE);

  const editingActionData = useMemo(() => {
    if (!editingTool || !actionsData?.data) return null;
    return actionsData.data.find((n) => n.type === editingTool.node_name);
  }, [editingTool, actionsData]);

  const [selectedActionName, setSelectedActionName] = useState<string | null>(
    editingTool?.node_name || null
  );

  // Fetch node config for the selected action
  const { data: nodeConfigData, isLoading: isLoadingNodeConfig } = useQuery({
    queryKey: ["node-config", selectedActionName],
    queryFn: async () => {
      if (!selectedActionName) return null;

      // Call the node's config endpoint with tool_mode
      const response = await NodesService.getNodeConfig({
        path: {
          group_id: spotGroup.id,
          node_id: selectedActionName,
        },
        body: {
          tool_mode: true,
        },
      });

      return response.data;
    },
    enabled: !!selectedActionName && currentStep === 2 && !isReadOnly,
  });

  const [actionTitle, actionImage] = useMemo(() => {
    if (!selectedActionName) return [null, null];

    let action: NodeDefinition | undefined;

    if (editingActionData) {
      action = editingActionData;
    } else {
      action = filteredActionsData.find((a) => a.type === selectedActionName);
    }

    return [action?.title, action?.image];
  }, [selectedActionName, filteredActionsData, editingActionData]);

  const [inputValue, setInputValue] = useState(
    editingTool?.extra_instructions?.text || ""
  );

  // Set initial step based on editing mode
  React.useEffect(() => {
    if (editingTool) {
      setCurrentStep(2); // Go directly to config step when editing
    }
  }, [editingTool]);

  const handleBack = useCallback(() => {
    if (currentStep === 2 && !editingTool) {
      setCurrentStep(1);
      setSelectedActionName(null);
    }
  }, [currentStep, editingTool]);

  const selectedActionIntegrationsMissing = useMemo(() => {
    if (!selectedActionName) return false;
    const action =
      editingActionData ||
      filteredActionsData.find((a) => a.type === selectedActionName);
    if (!action) return false;
    return hasMissingIntegrations(action);
  }, [
    selectedActionName,
    editingActionData,
    filteredActionsData,
    hasMissingIntegrations,
  ]);

  const handleSave = useCallback(() => {
    if (selectedActionName) {
      onSelectAction({
        node_name: selectedActionName,
        value: inputValue,
        tool_config: toolConfig,
      });
      onClose();
    }
  }, [selectedActionName, inputValue, toolConfig, onSelectAction, onClose]);

  const footerActions = useMemo(() => {
    if (currentStep === 1) {
      return [
        {
          label: "Cancel",
          props: {
            variant: "ghost",
            onClick: onClose,
          },
        },
      ];
    }

    // Step 2 - Configuration + Usage Conditions
    const actions = [];

    if (!editingTool) {
      actions.push({
        label: "Back",
        props: {
          variant: "ghost",
          onClick: handleBack,
          leftIcon: <ChevronLeftIcon size={12} />,
        },
      });
    }

    actions.push({
      label: editingTool ? "Save" : "Add action",
      props: {
        onClick: handleSave,
        colorScheme: "blue",
        disabled: !selectedActionName || selectedActionIntegrationsMissing,
      },
    });

    return actions;
  }, [
    currentStep,
    editingTool,
    handleSave,
    selectedActionName,
    selectedActionIntegrationsMissing,
    onClose,
    handleBack,
  ]);

  return (
    <BaseDialog
      isOpen={isOpen}
      onOpenChange={onClose}
      size="5xl"
      title={
        actionTitle ? (
          <div className="flex items-center gap-2">
            {actionImage && (
              <img
                src={actionImage}
                alt={actionTitle}
                className="w-6 h-6 rounded"
              />
            )}
            {actionTitle}
          </div>
        ) : (
          "Add action"
        )
      }
      description={
        currentStep !== 1
          ? "Configure parameters values to be AI-autofilled or fixed, and define the usage conditions of the tool."
          : undefined
      }
      footerActions={footerActions}
    >
      <div className="-m-1 overflow-x-hidden p-1">
        <AnimatePresence mode="wait" initial={false}>
          {currentStep === 1 ? (
            <motion.div
              key="step1"
              initial={{ x: "-100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "-100%", opacity: 0 }}
              transition={{ type: "tween", duration: 0.3 }}
              style={{ width: "100%" }}
            >
              <div>
                <div className="max-w-2xl mb-2">
                  <SearchBar
                    placeholder="Search actions"
                    setValue={setSearch}
                    value={search}
                    isDebounced
                    autoFocus
                  />
                </div>

                <div className="w-full">
                  <DataTable
                    isLoading={isLoadingActions}
                    columns={columns}
                    data={paginatedData}
                    rowOnClick={(id, value) => {
                      if (value?.type) {
                        setSelectedActionName(value.type);
                        setCurrentStep(2);
                      }
                    }}
                    loadingRows={8}
                    noDataMessage="No more actions available"
                    noShadow
                  />

                  {totalPages > 1 && (
                    <footer className="w-full pt-6 flex justify-center">
                      <PageChanger
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        totalPages={totalPages}
                        maxWidth="3rem"
                        size="sm"
                      />
                    </footer>
                  )}
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="step2"
              initial={{ x: "100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "100%", opacity: 0 }}
              transition={{ type: "tween", duration: 0.3 }}
              style={{ width: "100%" }}
            >
              <div className="w-full flex flex-col gap-6 items-stretch">
                <ActionConfigurationStep
                  nodeName={selectedActionName || ""}
                  toolConfig={toolConfig}
                  onToolConfigChange={setToolConfig}
                  configResponse={nodeConfigData || undefined}
                  isLoadingConfig={isLoadingNodeConfig}
                  integrations={
                    editingActionData?.integrations ||
                    filteredActionsData.find(
                      (a) => a.type === selectedActionName
                    )?.integrations ||
                    undefined
                  }
                />

                {!isLoadingNodeConfig && (
                  <div>
                    <Separator className="mb-6" />
                    <NoxusInput
                      label="Tool usage conditions"
                      placeholder="Specify when this action should be executed (e.g., 'Use when asked to list OneDrive files')"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      isTextarea
                    />
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </BaseDialog>
  );
};
